import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

function AnimatedBlock({
  children,
  delay = 0,
  direction = 'up',
}: {
  children: React.ReactNode;
  delay?: number;
  direction?: 'up' | 'left' | 'right';
}) {
  const { ref, visible } = useScrollAnimation(0.1);
  const translateMap = { up: 'translateY(40px)', left: 'translateX(-40px)', right: 'translateX(40px)' };

  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translate(0,0)' : translateMap[direction],
        transition: `opacity 0.8s ease ${delay}s, transform 0.8s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

export default function BeginningSection() {
  const { ref: titleRef, visible: titleVisible } = useScrollAnimation(0.2);

  const chapters = [
    {
      num: '01',
      label: '15살',
      title: '재봉틀 앞에 앉다',
      body: '플러스 사이즈였던 나는 마음에 드는 옷을 찾기 어려웠습니다. 그래서 직접 만들어 보기로 했습니다.',
      color: 'emerald',
      icon: 'ri-scissors-line',
    },
    {
      num: '02',
      label: '첫 작품',
      title: '멜빵 원피스',
      body: '재봉틀 앞에 앉아 만든 첫 옷은 활동하기 편한 멜빵 원피스였습니다. 처음 만든 옷이었지만 사람들은 말했습니다.',
      color: 'amber',
      icon: 'ri-shirt-line',
    },
    {
      num: '03',
      label: '깨달음',
      title: '기술의 발견',
      body: '기술은 사람을 작게 만드는 것이 아니라 사람을 당당하게 세운다는 것을. 그 경험은 한 사람의 삶을 바꾸었습니다.',
      color: 'emerald',
      icon: 'ri-lightbulb-line',
    },
    {
      num: '04',
      label: '공동체',
      title: 'Daygreen',
      body: '그 경험은 한 사람의 삶을 바꾸었고 지금은 하나의 공동체가 되었습니다.',
      color: 'stone',
      icon: 'ri-community-line',
    },
  ];

  return (
    <section className="py-32 px-6 bg-white overflow-hidden">
      <div className="max-w-6xl mx-auto">
        {/* 타이틀 */}
        <div
          ref={titleRef}
          className="mb-24"
          style={{
            opacity: titleVisible ? 1 : 0,
            transform: titleVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 0.9s ease',
          }}
        >
          <span className="text-emerald-600 text-sm font-semibold tracking-widest uppercase">Chapter 01</span>
          <h2
            className="text-5xl font-bold text-stone-800 mt-3 mb-6 leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            재봉틀 한 대에서
            <br />
            시작된 이야기
          </h2>
          <div className="w-16 h-0.5 bg-emerald-500"></div>
        </div>

        {/* 인트로 텍스트 + 이미지 */}
        <div className="grid md:grid-cols-2 gap-20 items-center mb-28">
          <AnimatedBlock direction="left" delay={0}>
            <p className="text-2xl text-stone-700 leading-relaxed mb-8" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
              어떤 산업은 거대한 공장에서 시작됩니다.
              <br />
              하지만 우리의 이야기는
              <br />
              <span className="text-emerald-700 font-semibold">작은 재봉틀 한 대에서 시작되었습니다.</span>
            </p>
            <div className="bg-stone-50 border-l-4 border-emerald-500 pl-6 py-4 rounded-r-xl">
              <p className="text-stone-600 text-base leading-relaxed italic" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
                "15살이 만들었는데 정말 잘 만들었다."
              </p>
              <p className="text-stone-400 text-sm mt-2">— 첫 작품을 본 사람들의 말</p>
            </div>
          </AnimatedBlock>

          <AnimatedBlock direction="right" delay={0.2}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/888e4cd7a531309676b73aa9f2750084.jpg"
                alt="시작 이야기"
                className="w-full h-[480px] object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              <div className="absolute bottom-6 left-6 bg-white/90 backdrop-blur-sm px-5 py-3 rounded-xl shadow">
                <span className="text-stone-800 font-bold text-sm" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
                  15세, 첫 재봉틀
                </span>
              </div>
            </div>
          </AnimatedBlock>
        </div>

        {/* 챕터 카드 타임라인 */}
        <div className="relative">
          {/* 연결선 */}
          <div className="hidden md:block absolute top-10 left-0 right-0 h-px bg-stone-200 z-0" style={{ top: '2.5rem' }}></div>

          <div className="grid md:grid-cols-4 gap-8 relative z-10">
            {chapters.map((ch, i) => (
              <AnimatedBlock key={i} direction="up" delay={i * 0.15}>
                <div className="bg-white border border-stone-100 rounded-2xl p-7 shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-default">
                  <div className="flex items-center gap-3 mb-5">
                    <div className={`w-10 h-10 rounded-full bg-${ch.color}-100 flex items-center justify-center`}>
                      <i className={`${ch.icon} text-${ch.color}-600 text-lg`}></i>
                    </div>
                    <span className="text-stone-300 font-bold text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      {ch.num}
                    </span>
                  </div>
                  <span className={`text-${ch.color}-600 text-xs font-semibold tracking-wider uppercase`}>{ch.label}</span>
                  <h3 className="text-lg font-bold text-stone-800 mt-1 mb-3" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
                    {ch.title}
                  </h3>
                  <p className="text-stone-500 text-sm leading-relaxed">{ch.body}</p>
                </div>
              </AnimatedBlock>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
